import { useEffect } from 'react';
import { Outlet, useLocation, useNavigationType } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Header from './Header';
import Footer from './Footer';
import CookieBanner from '@/components/legal/CookieBanner';
import BottomNavMobile from '@/components/nav/BottomNavMobile';
import BackToTop from '@/components/nav/BackToTop';
import GlobalSchema from '@/components/seo/GlobalSchema';
import PWAInstallPrompt from '@/components/widgets/PWAInstallPrompt';
// 2026-05-26 — Patch v8 : système Personas (5 canon + intent + pet_owner).
// Le Provider gère 3 clés localStorage (ip.personaSlug, ip.intentSlug,
// ip.petOwner) avec sync cross-tab. Composants downstream (PersonaBadge,
// PersonaCTA, StartHerePersonaPicker, usePersona hook) consomment ce contexte.
import { PersonaProvider } from '@/hooks/usePersona';
import { memoriser, doitRestaurer, positionMemorisee, allerA, cleCourante } from '@/utils/scrollMemory';
// 2026-05-16 — Phase 1 animations : halo curseur 32px qui suit la souris
// + change couleur sur les éléments interactifs. Visible uniquement desktop
// avec souris (pas d'effet sur mobile / pointer:coarse). Self-suppresses sur
// reduced-motion via le composant lui-même.
import CursorHalo from '@/components/effects/CursorHalo';
// 2026-06-16 — Animation scroll GLOBALE : révèle au scroll les <section> de
// CHAQUE page (sauf celles déjà gérées par RevealOnScroll). Opacity seule +
// fail-open. Voir src/components/effects/AutoReveal.jsx.
import AutoReveal from '@/components/effects/AutoReveal';
// 2026-06-20 — Feel premium au scroll : Lenis (smooth-scroll inertiel, coupé
// sous reduced-motion) + barre de progression de lecture (framer useScroll).
import SmoothScroll from '@/components/effects/SmoothScroll';
import ScrollProgress from '@/components/effects/ScrollProgress';

// 2026-05-25 — Audit design Sprint 1 (C·02, C·05) :
// Supprimé : NewsletterComeback, NewsletterSticky, NewsletterExitIntent
// (3 surfaces de capture email sur 5 = agression, contradictoire avec le
// registre éditorial). Capture conservée : NewsletterBanner home + page
// /newsletter + NewsletterInline contextuelle dans articles piliers.
// Supprimé aussi : BadgeUnlockToast, StickyBadgeWidget, SessionTimerTracker
// (gamification SaaS, hors registre magazine).

// Wraps every route. The ErrorBoundary lives at the root in main.jsx so
// catastrophic failures still report to Sentry. The cookie banner is mounted
// globally and self-dismisses once the user has chosen.
export default function Layout() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const { t } = useTranslation('common');

  // Position de lecture : on remonte en haut sur une NOUVELLE page, mais on
  // rend sa place au visiteur qui revient en arrière.
  //
  // 2026-07-21 — cet effet remettait à 0 sur `pathname` sans jamais regarder
  // le TYPE de navigation : un retour arrière était traité comme un nouveau
  // départ, et Jade devait « tout redescendre ». On distingue désormais POP
  // (retour/avance) de PUSH (clic sur un lien).
  //
  // Les deux passes d'origine restent nécessaires : une immédiate qui gagne
  // la course avec le commit de React, une à la frame suivante parce qu'un
  // chunk de route lazy peut allonger le document après coup (le fallback
  // Suspense fausse transitoirement la hauteur, ce qui piège le scroll).
  // `instant` est explicite : `auto` peut être lu comme « smooth » quand un
  // `scroll-behavior: smooth` global est actif.
  useEffect(() => {
    const cle = cleCourante();
    const cible = doitRestaurer(navigationType, cle) ? positionMemorisee(cle) : 0;

    allerA(cible);
    const id = requestAnimationFrame(() => allerA(cible));

    // ⚠️ Le nettoyage mémorise sous `cle` — l'entrée QU'ON QUITTE, celle que
    // cet effet a servie. Première version : elle enregistrait sous la clé
    // précédente, donc systématiquement décalée d'un cran ; le retour arrière
    // lisait une case jamais écrite et remontait en haut. Pris en défaut par
    // le test en navigateur, pas par le lint.
    return () => {
      cancelAnimationFrame(id);
      memoriser(cle);
    };
  }, [location.pathname, navigationType]);

  return (
    <PersonaProvider>
      <div className="flex min-h-screen flex-col bg-cream-50 text-navy-900 transition-colors duration-200 dark:bg-black dark:text-[#FAFAFA]">
        {/* Skip-to-content link — visible only when keyboard-focused. WCAG
            2.1 SC 2.4.1. Sits before <Header> so Tab from page load lands here. */}
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[100] focus:rounded-btn focus:bg-bordeaux-600 focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-cream-50 focus:shadow-lg focus:outline-none focus:ring-4 focus:ring-bordeaux-600/30"
        >
          {t('skipToContent')}
        </a>
        <GlobalSchema />
        <SmoothScroll />
        <ScrollProgress />
        <CursorHalo />
        <AutoReveal />
        <Header />
        {/* Bottom nav reserves 56px on mobile — pad main below the fold so the
            last article isn't hidden under the nav bar. */}
        {/* `key` par pathname → remonté à chaque nav, donc le fondu CSS
            `.page-fade` se rejoue (transition de page). Fail-safe : voir
            .page-fade dans index.css (opacité de base = 1). */}
        <main
          id="main"
          key={location.pathname}
          className="page-fade flex-1 pt-20 pb-16 lg:pb-0"
        >
          <Outlet />
        </main>
        <Footer />
        <CookieBanner />
        <BottomNavMobile />
        <BackToTop />
        {/* PWA install banner — mobile only, from 3rd visit onward. Listens for
            beforeinstallprompt (Android/Chrome) and shows iOS Safari hint as
            fallback. Auto-hides when running standalone. */}
        <PWAInstallPrompt />
      </div>
    </PersonaProvider>
  );
}
